"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.distanceToEntities = distanceToEntities;
var _vector = require("./vector");
var _distanceToLineSegment = require("./distanceToLineSegment");
var _checkAngles = require("./checkAngles");
/**
 * Calculates the minimum distance between a point p and a list of entities. If includeEndPoints is false (default),
 * then the point must be directly "above" or "below" an entity, it will not compute a distance if the point is beyond
 * the end of the entity.
 * @param p The point to test
 * @param entities An array of entities (LINEs or ARCs) to test.
 * @param includeEndPoints Whether to count distance to endpoints. If so, a point can be "outside" the entity and still
 * return a solution. Default: false.
 * @returns Returns an object containing the following information: the minimum distance, the entity it is nearest to,
 * and the nearest point on that entity, both in cartesian coordinates and in parametric coordinates (0..1) on that
 * entity. If `includeEndPoints` is false and the point lies outside of all entities, or if entities is an empty array,
 * returns null.
 */
function distanceToEntities(p, entities, includeEndPoints = false) {
  // TODO: Have a argument to say, if it lands on the end of an entity, move to the next? Or vice versa? How can this function possibly know which direction is the preferred?

  let distance;
  let nearestEntity;
  let nearestPoint;
  let nearestParametric;
  for (let e of entities) {
    switch (e.type) {
      case 'LINE':
        {
          let d = (0, _distanceToLineSegment.distanceToLineSegment)(p.x, p.y, e.start.x, e.start.y, e.end.x, e.end.y, includeEndPoints);
          if (d !== null && (typeof distance === 'undefined' || d.distance < distance)) {
            distance = d.distance;
            nearestEntity = e;
            nearestPoint = d.closestPoint;
            nearestParametric = d.t;
          }
          break;
        }
      case 'ARC':
        {
          let angleToPoint = Math.atan2(p.y - e.y, p.x - e.x);

          // Is the angle between the start and end angles?
          // biarc.js always returns arc that subtend an angle less than 180 degrees.
          // atan2 always returns angles that are between -pi and pi.
          // We need to know if angleToPoint is "between" startAngle and endAngle.

          // TODO: Make this work for arcs greater than 180 degrees

          // First, rotate all the angles to put startAngle at 0
          // Then:
          // 1. endAngle and angleToPoint must have the same sign
          // 2. |angleToPoint| < |endAngle| 

          // let rEndAngle = e.endAngle - e.startAngle
          // if (rEndAngle > Math.PI) rEndAngle -= 2 * Math.PI
          // if (rEndAngle <= -Math.PI) rEndAngle += 2 * Math.PI

          // let rAngleToPoint = angleToPoint - e.startAngle
          // if (rAngleToPoint > Math.PI) rAngleToPoint -= 2 * Math.PI
          // if (rAngleToPoint <= -Math.PI) rAngleToPoint += 2 * Math.PI

          // const isWithinArc = (Math.sign(rAngleToPoint) === Math.sign(rEndAngle) || Math.abs(rAngleToPoint) < 1e-15) && Math.abs(rAngleToPoint) <= Math.abs(rEndAngle)

          // Update to allow > 180 degs
          // let rEndAngle = e.endAngle - e.startAngle
          // while (rEndAngle >= 2 * Math.PI) rEndAngle -= 2 * Math.PI
          // while (rEndAngle < 0) rEndAngle += 2 * Math.PI

          // let rAngleToPoint = angleToPoint - e.startAngle
          // while (rAngleToPoint >= 2 * Math.PI) rAngleToPoint -= 2 * Math.PI
          // while (rAngleToPoint < 0) rAngleToPoint += 2 * Math.PI

          // Update to allow all angles, hopefully
          (0, _checkAngles.checkAngles)(e);
          let rEndAngle = e.endAngle - e.startAngle;
          while (angleToPoint >= e.startAngle) angleToPoint -= 2 * Math.PI;
          while (angleToPoint < e.startAngle) angleToPoint += 2 * Math.PI;
          let rAngleToPoint = angleToPoint - e.startAngle;
          const isWithinArc = rAngleToPoint <= rEndAngle;
          if (isWithinArc) {
            let d = Math.abs(e.r - (0, _vector.norm)((0, _vector.sub)(p, e))); // Note: e is not strictly a point but it still has x and y properties
            if (typeof distance === 'undefined' || d < distance) {
              distance = d;
              nearestEntity = e;
              nearestPoint = {
                x: e.x + e.r * Math.cos(angleToPoint),
                y: e.y + e.r * Math.sin(angleToPoint)
              };
              nearestParametric = rAngleToPoint === 0 ? 0 : rAngleToPoint / rEndAngle;
            }
          } else if (includeEndPoints) {
            let arcPointStart = {
              x: e.x + e.r * Math.cos(e.startAngle),
              y: e.y + e.r * Math.sin(e.startAngle)
            };
            let arcPointEnd = {
              x: e.x + e.r * Math.cos(e.endAngle),
              y: e.y + e.r * Math.sin(e.endAngle)
            };
            let dStart = (0, _vector.norm)((0, _vector.sub)(arcPointStart, p));
            let dEnd = (0, _vector.norm)((0, _vector.sub)(arcPointEnd, p));
            if (typeof distance === 'undefined' || dStart < distance) {
              distance = dStart;
              nearestEntity = e;
              nearestPoint = arcPointStart;
              nearestParametric = 0;
            }
            if (typeof distance === 'undefined' || dEnd < distance) {
              distance = dEnd;
              nearestEntity = e;
              nearestPoint = arcPointEnd;
              nearestParametric = 1;
            }
          }
          break;
        }
      case 'CIRCLE':
        {
          let d = Math.abs(e.r - (0, _vector.norm)((0, _vector.sub)(p, e))); // Note: e is not strictly a point but it still has x and y properties
          if (typeof distance === 'undefined' || d < distance) {
            // TODO: Could also scale point instead of using trig 
            let angleToPoint = Math.atan2(p.y - e.y, p.x - e.x);
            distance = d;
            nearestEntity = e;
            nearestPoint = {
              x: e.x + e.r * Math.cos(angleToPoint),
              y: e.y + e.r * Math.sin(angleToPoint)
            };
            if (angleToPoint < 0) angleToPoint += 2 * Math.PI;
            nearestParametric = angleToPoint / (2 * Math.PI);
          }
          break;
        }
      default:
        throw new Error('Unsupported entity type: ' + e.type);
    }
  }
  if (distance !== undefined && nearestEntity !== undefined && nearestPoint !== undefined && nearestParametric !== undefined) {
    return {
      distance,
      nearestEntity,
      nearestPoint,
      nearestParametric
    };
  } else {
    return null;
  }
}